/**
 * The record the effects comparer writes when a person compares an effect with Phira (W3-04; Appendix C H5). It is
 * evidence, not configuration: the orchestrator stores the JSON next to the captures (docs/verification/effects/) and,
 * for a built-in that matches, adds `signOffEntry(record)` to `SIGNED_OFF` (builtin/signOff.ts), which drops that
 * built-in's "approximate" badge.
 */

import type { BuiltinShader } from '../../../../core/model/types';
import type { BuiltinSignOff } from '../builtin/signOff';
import type { DiffStats } from './compare';

/** What the comparer ran: a built-in, or the custom UV orientation probe. */
export type ComparedEffect = BuiltinShader | 'uvProbe';

export interface SignOffRecord {
  readonly kind: 'phiedit-effect-comparison';
  readonly version: 1;
  readonly effect: ComparedEffect;
  readonly verdict: 'matches' | 'differs';
  /** Uniform values used (colours 0..1), as in the capture kit's extra.json. */
  readonly vars: Readonly<Record<string, number | readonly number[]>>;
  readonly time: number;
  readonly scope: 'global' | 'chart';
  /** Size of the Phira capture (null when none was loaded). */
  readonly capture: { readonly width: number; readonly height: number } | null;
  readonly stats: DiffStats | null;
  readonly date: string;
  readonly tester: string;
  readonly phira: string;
  readonly notes: string;
}

export interface SignOffInput {
  readonly effect: ComparedEffect;
  readonly verdict: 'matches' | 'differs';
  readonly vars: ReadonlyArray<readonly [string, number | readonly number[]]>;
  readonly time: number;
  readonly scope: 'global' | 'chart';
  readonly capture: { readonly width: number; readonly height: number } | null;
  readonly stats: DiffStats | null;
  readonly tester: string;
  readonly phira: string;
  readonly notes: string;
  /** Defaults to today. */
  readonly now?: Date;
}

const round = (v: number): number => (Number.isFinite(v) ? Math.round(v * 1e4) / 1e4 : v);

/**
 * Whether the effect's shader reads the `time` uniform: Glitch (its bands change with chart time) and custom shaders
 * (a package shader may use `time`). The other built-ins ignore it, so a leftover Time value is not evidence for them.
 */
export function readsTime(effect: ComparedEffect): boolean {
  return effect === 'glitch' || effect === 'uvProbe';
}

export function signOffRecord(i: SignOffInput): SignOffRecord {
  const vars: Record<string, number | readonly number[]> = {};
  for (const [name, value] of i.vars) vars[name] = typeof value === 'number' ? round(value) : value.map(round);
  const stats =
    i.stats === null
      ? null
      : { mean: round(i.stats.mean), psnr: round(i.stats.psnr), over: round(i.stats.over), max: i.stats.max };
  return {
    kind: 'phiedit-effect-comparison',
    version: 1,
    effect: i.effect,
    verdict: i.verdict,
    vars,
    // Only effects that read `time` record it (Glitch and custom shaders); the rest write 0.
    time: round(readsTime(i.effect) ? i.time : 0),
    scope: i.scope,
    capture: i.capture,
    stats,
    date: (i.now ?? new Date()).toISOString().slice(0, 10),
    tester: i.tester.trim(),
    phira: i.phira.trim(),
    notes: i.notes.trim(),
  };
}

/** The record as pretty JSON (Infinity, which JSON lacks, as the string "Infinity"). */
export function signOffJson(r: SignOffRecord): string {
  return `${JSON.stringify(r, (_k, v: unknown) => (v === Infinity ? 'Infinity' : v), 2)}\n`;
}

/** File name of a record: `<effect>-<date>.json`. */
export function signOffFileName(r: SignOffRecord): string {
  return `${r.effect}-${r.date}.json`;
}

/**
 * The `SIGNED_OFF` entry of a matching built-in record, with the evidence path it will be stored at; null for the
 * probe, a record that does not match, or one with no Phira capture (an entry without capture evidence is not a
 * sign-off).
 */
export function signOffEntry(
  r: SignOffRecord,
  evidenceDir = 'docs/verification/effects',
): { readonly name: BuiltinShader; readonly entry: BuiltinSignOff } | null {
  if (r.effect === 'uvProbe' || r.verdict !== 'matches' || r.capture === null) return null;
  return {
    name: r.effect,
    entry: { date: r.date, tester: r.tester, phira: r.phira, evidence: `${evidenceDir}/${signOffFileName(r)}` },
  };
}
